import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ShoppingBag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { checkoutAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useSEO } from '../hooks/useSEO';

const MAX_POLLS = 8;        // 8 × 2s = 16s
const POLL_INTERVAL_MS = 2000;

const CheckoutSuccessPage = () => {
  useSEO({
    title: 'Order Confirmed',
    description: 'Your BlueSpark order has been confirmed.',
    noindex: true,
  });
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('loading'); // loading | paid | failed | expired | timeout
  const [order, setOrder] = useState(null);
  const polls = useRef(0);
  const timerRef = useRef(null);
  const { clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) { setState('failed'); return; }

    const poll = async () => {
      try {
        const data = await checkoutAPI.getStatus(sessionId);
        if (data.payment_status === 'paid') {
          setOrder(data);
          setState('paid');
          clearCart();
          return;
        }
        if (data.status === 'expired') {
          setState('expired');
          return;
        }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) {
          setState('timeout');
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        polls.current += 1;
        if (polls.current >= MAX_POLLS) {
          setState('failed');
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 bg-gray-50 py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card>
            <CardContent className="p-8 md:p-12 text-center">
              {state === 'loading' && (
                <>
                  <Loader2 className="h-12 w-12 mx-auto text-blue-600 animate-spin mb-4" />
                  <h1 className="text-2xl font-bold mb-2">Confirming your payment…</h1>
                  <p className="text-gray-600">Just a moment while Stripe finalises your order.</p>
                </>
              )}

              {state === 'paid' && order && (
                <>
                  <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                  </div>
                  <h1 className="text-3xl font-bold mb-2" data-testid="success-heading">Thank you for your order!</h1>
                  <p className="text-gray-600 mb-6">Your payment was successful and your order is being prepared.</p>

                  <div className="bg-gray-50 rounded-lg p-4 text-left mb-6 space-y-2">
                    {order.order_id && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Order ID</span>
                        <span className="font-mono font-medium">{order.order_id}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Amount paid</span>
                      <span className="font-bold">
                        ${order.amount?.toFixed(2)} {(order.currency || 'AUD').toUpperCase()}
                      </span>
                    </div>
                    {order.items?.length > 0 && (
                      <div className="border-t pt-2 mt-2 space-y-1">
                        {order.items.map((it, idx) => (
                          <div key={idx} className="flex justify-between text-xs text-gray-600">
                            <span className="truncate pr-2">{it.name} × {it.qty}</span>
                            <span>${it.line_total?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-gray-500 mb-6">
                    A receipt has been emailed to you by Stripe. We'll ship Australia-wide within 3–7 business days.
                  </p>

                  <div className="flex flex-col md:flex-row gap-2 justify-center">
                    <Link to="/">
                      <Button variant="outline" data-testid="back-to-home">Back to Home</Button>
                    </Link>
                    <Link to="/products">
                      <Button data-testid="continue-shopping">
                        <ShoppingBag className="h-4 w-4 mr-2" /> Continue Shopping
                      </Button>
                    </Link>
                  </div>
                </>
              )}

              {state === 'expired' && (
                <>
                  <XCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
                  <h1 className="text-2xl font-bold mb-2">Checkout session expired</h1>
                  <p className="text-gray-600 mb-6">The payment session timed out. Please try again.</p>
                  <Link to="/cart"><Button>Back to Cart</Button></Link>
                </>
              )}

              {state === 'timeout' && (
                <>
                  <Loader2 className="h-12 w-12 mx-auto text-amber-500 mb-4" />
                  <h1 className="text-2xl font-bold mb-2">Still processing…</h1>
                  <p className="text-gray-600 mb-6">
                    Your payment is taking longer than usual to confirm. You'll receive an email from Stripe once it's complete.
                  </p>
                  <Link to="/"><Button variant="outline">Back to Home</Button></Link>
                </>
              )}

              {state === 'failed' && (
                <>
                  <XCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
                  <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
                  <p className="text-gray-600 mb-6">We couldn't confirm your payment. If you were charged, please contact support.</p>
                  <Link to="/cart"><Button>Back to Cart</Button></Link>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
